import { JSONPath } from 'jsonpath-plus';
import { countGraphemes } from 'unicode-segmenter/grapheme';
import { msToStamp, pad } from '../Util.js';
function toString(v) {
    if (v === undefined)
        return '';
    return v + '';
}
function toDate(v) {
    let d;
    if (v instanceof Date)
        d = v;
    else if (typeof v === 'number')
        d = new Date(v);
    else if (typeof v === 'string' && v.trim().length > 0) {
        const num = Number(v);
        d = new Date(isNaN(num) ? v : num);
    }
    return d && !isNaN(d.getTime()) ? d : null;
}
const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
function getDatePart(v, tz, key) {
    const d = toDate(v);
    if (!d)
        return null;
    const parts = getDateParts(d, tz);
    return parts ? parts[key] : null;
}
const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function getDateParts(d, tz) {
    if (!tz) {
        return {
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate(),
            hour: d.getHours(),
            minute: d.getMinutes(),
            second: d.getSeconds(),
            weekday: d.getDay(),
        };
    }
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            weekday: 'short',
            hourCycle: 'h23',
        }).formatToParts(d);
        const get = (type) => Number(parts.find((p) => p.type === type)?.value);
        const wd = parts.find((p) => p.type === 'weekday')?.value;
        const weekday = wd !== undefined ? weekdayMap[wd] : undefined;
        if (weekday === undefined)
            return null;
        const out = {
            year: get('year'),
            month: get('month'),
            day: get('day'),
            hour: get('hour'),
            minute: get('minute'),
            second: get('second'),
            weekday,
        };
        return Object.values(out).some((v) => isNaN(v)) ? null : out;
    }
    catch {
        return null;
    }
}
// Note: when adding new functions, make sure to update the docs!
export const ExpressionFunctions = {
    // General operations
    length: (v) => {
        let len = 0;
        if (v === undefined || v === null) {
            len = 0;
        }
        else if (Array.isArray(v)) {
            len = v.length;
        }
        else if (typeof v === 'number') {
            len = (v + '').length;
        }
        else if (typeof v === 'bigint') {
            len = v.toString().length;
        }
        else if (typeof v === 'string') {
            // So we handle UTF graphemes correctly
            len = countGraphemes(v);
        }
        else if (v instanceof RegExp) {
            len = v.toString().length;
        }
        else if (typeof v === 'object') {
            len = Object.keys(v).length;
        }
        else {
            // If it's got to here, we don't know how to handle it
            len = NaN;
        }
        return len;
    },
    // Number operations
    // TODO: round to fractionals, without fp issues
    round: (v) => Math.round(v),
    floor: (v) => Math.floor(v),
    ceil: (v) => Math.ceil(v),
    abs: (v) => Math.abs(v),
    fromRadix: (v, radix) => parseInt(v, radix || 10),
    toRadix: (v, radix) => v.toString(radix || 10),
    toFixed: (v, dp) => Number(v).toFixed(Math.max(0, dp || 0)),
    isNumber: (v) => !isNaN(v),
    max: (...args) => Math.max(...args),
    min: (...args) => Math.min(...args),
    randomInt: (min = 0, max = 10) => {
        min = Number(min);
        max = Number(max);
        return min + Math.round(Math.random() * (max - min));
    },
    log: (v) => Math.log(v),
    log10: (v) => Math.log10(v),
    // String operations
    trim: (v) => toString(v).trim(),
    strlen: (v) => toString(v).length,
    substr: (str, start, end) => {
        return toString(str).slice(start, end);
    },
    split: (str, separator) => {
        if (separator === undefined) {
            return [toString(str)];
        }
        return toString(str).split(toString(separator));
    },
    join: (arr = [], separator = ',') => {
        return (Array.isArray(arr) ? arr.map(toString) : [toString(arr)]).join(toString(separator));
    },
    concat: (...strs) => ''.concat(...strs.map(toString)),
    includes: (str, arg) => {
        return toString(str).includes(toString(arg));
    },
    indexOf: (str, arg, offset) => {
        return toString(str).indexOf(toString(arg), offset);
    },
    lastIndexOf: (str, arg, offset) => {
        return toString(str).lastIndexOf(toString(arg), offset);
    },
    toUpperCase: (str) => {
        return toString(str).toUpperCase();
    },
    toLowerCase: (str) => {
        return toString(str).toLowerCase();
    },
    replaceAll: (str, find, replace) => {
        return toString(str).replaceAll(toString(find), toString(replace));
    },
    decode: (str, enc) => {
        if (enc === undefined) {
            enc = 'latin1';
        }
        else {
            enc = '' + enc;
        }
        return Buffer.from(toString(str), enc).toString('latin1');
    },
    encode: (str, enc) => {
        if (enc === undefined) {
            enc = 'latin1';
        }
        else {
            enc = '' + enc;
        }
        return Buffer.from(toString(str)).toString(enc);
    },
    encodeURI: (str) => {
        return encodeURI(toString(str));
    },
    decodeURI: (str) => {
        return decodeURI(toString(str));
    },
    encodeURIComponent: (str) => {
        return encodeURIComponent(toString(str));
    },
    decodeURIComponent: (str) => {
        return decodeURIComponent(toString(str));
    },
    // Bool operations
    bool: (v) => !!v && v !== 'false' && v !== '0',
    // Object/array operations
    jsonpath: (obj, path) => {
        const shouldParseInput = typeof obj === 'string';
        if (shouldParseInput) {
            try {
                obj = JSON.parse(obj);
            }
            catch (_e) {
                // Ignore
            }
        }
        const value = JSONPath({
            wrap: false,
            path: toString(path),
            json: obj,
        });
        if (shouldParseInput && typeof value !== 'number' && typeof value !== 'string' && value) {
            try {
                return JSON.stringify(value);
            }
            catch (_e) {
                // Ignore
            }
        }
        return value;
    },
    jsonparse: (str) => {
        try {
            return JSON.parse(toString(str));
        }
        catch (_e) {
            return null;
        }
    },
    jsonstringify: (obj) => {
        try {
            return JSON.stringify(obj);
        }
        catch (_e) {
            return null;
        }
    },
    arrayIncludes: (arr, val) => {
        if (!Array.isArray(arr))
            return false;
        return arr.includes(val);
    },
    arrayIndexOf: (arr, val, offset) => {
        if (!Array.isArray(arr))
            return false;
        return arr.indexOf(val, offset);
    },
    arrayLastIndexOf: (arr, val, offset) => {
        if (!Array.isArray(arr))
            return false;
        return arr.lastIndexOf(val, offset ?? arr.length);
    },
    // Time operations
    unixNow: () => Date.now(),
    timestampToSeconds: (str) => {
        const match = (str + '').match(/^(\d+):(\d+):(\d+)$/i);
        if (match) {
            return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]);
        }
        else {
            return 0;
        }
    },
    secondsToTimestamp: (v, type) => {
        type = type ? type : 'nHH:mm:ss';
        v = v * 1000;
        return msToStamp(v, type);
    },
    msToTimestamp: (v, type) => {
        type = type ? type : 'nmm:ss.S';
        return msToStamp(v, type);
    },
    timeOffset: (time, offset, hr12 = false) => {
        const date = new Date();
        date.setHours(time.split(':')[0]);
        date.setMinutes(time.split(':')[1]);
        date.setSeconds(time.split(':')[2] || 0);
        let diff = offset;
        if (typeof offset === 'string') {
            let hours = 0;
            let minutes = 0;
            let seconds = 0;
            const negative = diff.startsWith('-');
            if (diff.startsWith('+') || diff.startsWith('-')) {
                diff = diff.substr(1);
            }
            if (offset.includes(':')) {
                const split = diff.split(':');
                hours = parseInt(split[0]) || 0;
                minutes = parseInt(split[1]) || 0;
                seconds = parseInt(split[2]) || 0;
            }
            else {
                hours = parseInt(diff) || 0;
            }
            date.setHours(date.getHours() + (negative ? -hours : hours));
            date.setMinutes(date.getMinutes() + (negative ? -minutes : minutes));
            date.setSeconds(date.getSeconds() + (negative ? -seconds : seconds));
        }
        else {
            date.setHours(date.getHours() + diff);
        }
        const displayHours = pad(date.getHours() > 12 && hr12 ? date.getHours() - 12 : date.getHours(), '0', 2);
        const displayMinutes = pad(date.getMinutes(), '0', 2);
        const displaySeconds = pad(date.getSeconds(), '0', 2);
        if (time.split(':').length === 2) {
            return `${displayHours}:${displayMinutes}`;
        }
        else if (time.split(':').length === 3) {
            return `${displayHours}:${displayMinutes}:${displaySeconds}`;
        }
        else {
            return '';
        }
    },
    timeDiff: (from, to) => {
        let diff = 0;
        let fromDate = new Date();
        let toDate = new Date();
        if (from.includes('T')) {
            fromDate = new Date(from);
        }
        else {
            fromDate.setHours(from.split(':')[0]);
            fromDate.setMinutes(from.split(':')[1]);
            fromDate.setSeconds(from.split(':')[2] || 0);
        }
        if (to.includes('T')) {
            toDate = new Date(to);
        }
        else {
            toDate.setHours(to.split(':')[0]);
            toDate.setMinutes(to.split(':')[1]);
            toDate.setSeconds(to.split(':')[2] || 0);
        }
        diff = toDate.getTime() - fromDate.getTime();
        if (isNaN(diff))
            return 'ERR';
        return Math.round(diff / 1000);
    },
    // Date operations
    parseDate: (v) => {
        const d = toDate(v);
        return d ? d.getTime() : null;
    },
    dateYear: (v, tz) => getDatePart(v, tz, 'year'),
    dateMonth: (v, tz) => getDatePart(v, tz, 'month'),
    dateDay: (v, tz) => getDatePart(v, tz, 'day'),
    dateHour: (v, tz) => getDatePart(v, tz, 'hour'),
    dateMinute: (v, tz) => getDatePart(v, tz, 'minute'),
    dateSecond: (v, tz) => getDatePart(v, tz, 'second'),
    dateWeekday: (v, tz) => getDatePart(v, tz, 'weekday'),
    dateFormat: (v, fmt, tz) => {
        const d = toDate(v);
        if (!d) {
            return '';
        }
        const format = fmt ? toString(fmt) : 'YYYY-MM-DDTHH:mm:ss';
        const formatLower = format.toLowerCase();
        if (formatLower === 'iso' || formatLower === 'iso8601') {
            return d.toISOString();
        }
        const parts = getDateParts(d, tz);
        if (!parts) {
            return '';
        }
        const hours12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
        // dayjs-compatible format tokens, sorted longest-first for greedy matching
        const tokens = {
            YYYY: String(parts.year),
            YY: String(parts.year).slice(-2),
            MMMM: monthNames[parts.month - 1],
            MMM: monthShort[parts.month - 1],
            MM: pad(parts.month, '0', 2),
            M: String(parts.month),
            dddd: dayNames[parts.weekday],
            ddd: dayShort[parts.weekday],
            DD: pad(parts.day, '0', 2),
            D: String(parts.day),
            HH: pad(parts.hour, '0', 2),
            H: String(parts.hour),
            hh: pad(hours12, '0', 2),
            h: String(hours12),
            mm: pad(parts.minute, '0', 2),
            m: String(parts.minute),
            ss: pad(parts.second, '0', 2),
            s: String(parts.second),
            SSS: pad(d.getMilliseconds(), '0', 3),
            A: parts.hour >= 12 ? 'PM' : 'AM',
            a: parts.hour >= 12 ? 'pm' : 'am',
        };
        const sortedKeys = Object.keys(tokens).sort((a, b) => b.length - a.length);
        let result = '';
        let i = 0;
        while (i < format.length) {
            let matched = false;
            for (const key of sortedKeys) {
                if (format.startsWith(key, i)) {
                    result += tokens[key];
                    i += key.length;
                    matched = true;
                    break;
                }
            }
            if (!matched) {
                result += format[i];
                i++;
            }
        }
        return result;
    },
    dateAdd: (v, amount, unit) => {
        const d = toDate(v);
        if (!d) {
            return null;
        }
        amount = Number(amount);
        if (!Number.isFinite(amount)) {
            return null;
        }
        unit = toString(unit).toLowerCase();
        const result = new Date(d.getTime());
        switch (unit) {
            case 'second':
            case 'seconds':
                result.setSeconds(result.getSeconds() + amount);
                break;
            case 'minute':
            case 'minutes':
                result.setMinutes(result.getMinutes() + amount);
                break;
            case 'hour':
            case 'hours':
                result.setHours(result.getHours() + amount);
                break;
            case 'day':
            case 'days':
                result.setDate(result.getDate() + amount);
                break;
            case 'week':
            case 'weeks':
                result.setDate(result.getDate() + amount * 7);
                break;
            case 'month':
            case 'months':
                result.setMonth(result.getMonth() + amount);
                break;
            case 'year':
            case 'years':
                result.setFullYear(result.getFullYear() + amount);
                break;
            default:
                return null;
        }
        const ts = result.getTime();
        return Number.isFinite(ts) ? ts : null;
    },
};
//# sourceMappingURL=ExpressionFunctions.js.map